import { endOfMonth, isBefore } from 'date-fns';
import i18next from 'i18next';
import { EObjective } from '../pages/campaign/types/creative';
import type {
  IBrandAwarenessCreative,
  ITrafficSalesCreative,
} from '../pages/campaign/types/creative';

const localeForCurrency = (currency: string) =>
  currency.toUpperCase() === 'IDR' ? 'id-ID' : 'en-US';

export const formatCurrency = (amount: number, currency: string = 'USD') => {
  const normalizedCurrency = currency.toUpperCase();
  const locale = localeForCurrency(normalizedCurrency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: normalizedCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })?.format(amount);
};

/**
 * Exact amount for anything a customer pays or owes: shows cents when there
 * are any (712.40 stays 712.40) — unlike `formatCurrency`, which rounds to
 * whole units for dashboards.
 */
export const formatPaymentAmount = (
  amount: number,
  currency: string = 'USD'
) => {
  const normalizedCurrency = currency.toUpperCase();
  // Whole amounts without decimals; anything else always with two, so a
  // price never shows as "712.4".
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  const digits = hasCents ? 2 : 0;
  return new Intl.NumberFormat(localeForCurrency(normalizedCurrency), {
    style: 'currency',
    currency: normalizedCurrency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
};

/**
 * The locale dates are written in, from the app's language rather than the
 * browser's — an Indonesian UI on an English browser must still read
 * "14 Okt 2026", not "Oct 14, 2026".
 */
export const getDateLocale = (): string =>
  i18next.language?.toLowerCase().startsWith('id') ? 'id-ID' : 'en-GB';

/** A YYYY-MM-DD (or ISO) date in the app's language, e.g. "14 Oct 2026". */
export const formatShortDate = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(getDateLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
};

export const formatReachNumber = (num: number) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`;
  }
  return num.toString();
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  IDR: 'Rp',
  GHS: 'GH₵',
  ZAR: 'R',
  KES: 'KSh',
  UGX: 'USh',
  TZS: 'TSh',
  RWF: 'RF',
  XAF: 'FCFA',
  XOF: 'CFA',
};

const symbolFor = (currency: string): string =>
  CURRENCY_SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;

/**
 * Compact currency formatter for stat cards: 1234 → "$1.2K", 1_500_000 → "$1.5M".
 * Falls back to the symbol map; for sub-1K values shows two-decimal precision.
 */
export const formatCompactCurrency = (
  amount: number,
  currency: string = 'USD'
) => {
  const symbol = symbolFor(currency);
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${symbol}${(amount / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${symbol}${(amount / 1_000).toFixed(1)}K`;
  if (abs >= 100) return `${symbol}${amount.toFixed(0)}`;
  return `${symbol}${amount.toFixed(2)}`;
};

export const formatRoas = (multiplier: number) => `${multiplier.toFixed(1)}x`;

export const formatDateRange = (flightDates?: {
  startDate?: Date | string;
  endDate?: Date | string;
}) => {
  const startDate = toValidDate(flightDates?.startDate);
  const endDate = toValidDate(flightDates?.endDate);

  if (startDate && endDate) {
    return `${startDate.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
    })} - ${endDate.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
    })}`;
  }

  // Fallback to calculated dates
  const fallbackStartDate = new Date();
  const fallbackEndDate = new Date();
  fallbackEndDate.setDate(fallbackStartDate.getDate() + 7); // Default 7-day campaign

  return `${fallbackStartDate.toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
  })} - ${fallbackEndDate.toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
  })}`;
};

export const getCampaignDuration = (
  startDate: Date | string,
  endDate: Date | string
) => {
  const parsedStartDate = toValidDate(startDate);
  const parsedEndDate = toValidDate(endDate);

  if (!parsedStartDate || !parsedEndDate) return 0;

  const timeDiff = parsedEndDate.getTime() - parsedStartDate.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
};

const toValidDate = (value?: Date | string | number | null) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

type TAudience = {
  selectedSegments?: string[];
  selectedAudiences?: string[];
  description: string;
};

export const getLimitedAudienceSummary = (audience: TAudience) => {
  const {
    selectedAudiences = [],
    selectedSegments = [],
    description,
  } = audience;

  if (description && description.trim()) {
    // For description, show first 50 characters with ellipsis if longer
    return description.length > 50
      ? `${description.substring(0, 50)}...`
      : description;
  }

  const allAudiences = [...selectedAudiences, ...selectedSegments];
  if (allAudiences.length === 0) {
    return 'No audience selected';
  }

  if (allAudiences.length <= 2) {
    return allAudiences.join(', ');
  }

  return `${allAudiences.slice(0, 2).join(', ')}`;
};

export const getAllAudiences = (audience: TAudience) => {
  const {
    selectedAudiences = [],
    selectedSegments = [],
    description,
  } = audience;

  if (description && description.trim()) {
    return [description];
  }

  return [...selectedAudiences, ...selectedSegments];
};

export const hasMoreThanTwoAudiences = (audience: TAudience) => {
  const {
    selectedAudiences = [],
    selectedSegments = [],
    description,
  } = audience;

  if (description && description.trim()) {
    return description.length > 50;
  }

  const allAudiences = [...selectedAudiences, ...selectedSegments];
  return allAudiences.length > 2;
};

export const getMoreAudiencesText = (audience: TAudience) => {
  const {
    selectedAudiences = [],
    selectedSegments = [],
    description,
  } = audience;

  if (description && description.trim()) {
    return 'see full';
  }

  const allAudiences = [...selectedAudiences, ...selectedSegments];
  return `+${allAudiences.length - 2} more`;
};

export const getConversionTrackingSummary = (tracking?: {
  online: boolean;
  offline: boolean;
}) => {
  if (!tracking?.online && !tracking?.offline) {
    return 'None configured';
  }

  const methods = [];
  if (tracking.online) methods.push('Online');
  if (tracking.offline) methods.push('Offline');

  return methods.join(' & ');
};

export const getCreativeHeadline = (
  displayAd?:
    | IBrandAwarenessCreative['displayAd']
    | ITrafficSalesCreative['displayAd'],
  richNotification?:
    | IBrandAwarenessCreative['richNotification']
    | ITrafficSalesCreative['richNotification'],
  videoAd?:
    | IBrandAwarenessCreative['videoAd']
    | ITrafficSalesCreative['videoAd']
) => {
  return (
    displayAd?.headline ||
    richNotification?.headline ||
    videoAd?.headline ||
    '[Headline not provided]'
  );
};

export const getCreativeCallToAction = (
  displayAd?:
    | IBrandAwarenessCreative['displayAd']
    | ITrafficSalesCreative['displayAd'],
  richNotification?:
    | IBrandAwarenessCreative['richNotification']
    | ITrafficSalesCreative['richNotification'],
  videoAd?:
    | IBrandAwarenessCreative['videoAd']
    | ITrafficSalesCreative['videoAd']
) => {
  return (
    displayAd?.callToAction?.replace('-', ' ') ||
    richNotification?.callToAction?.replace('-', ' ') ||
    videoAd?.callToAction?.replace('-', ' ') ||
    ''
  );
};

export const getCreativeCouponCode = (
  displayAd?:
    | IBrandAwarenessCreative['displayAd']
    | ITrafficSalesCreative['displayAd'],
  richNotification?:
    | IBrandAwarenessCreative['richNotification']
    | ITrafficSalesCreative['richNotification'],
  sms?:
    | IBrandAwarenessCreative['smsNotification']
    | ITrafficSalesCreative['smsNotification'],
  videoAd?:
    | IBrandAwarenessCreative['videoAd']
    | ITrafficSalesCreative['videoAd']
) => {
  return (
    displayAd?.couponCode ||
    richNotification?.couponCode ||
    sms?.couponCode ||
    videoAd?.couponCode ||
    ''
  );
};
export const getCreativeDescription = (
  richNotification?:
    | IBrandAwarenessCreative['richNotification']
    | ITrafficSalesCreative['richNotification'],
  sms?:
    | IBrandAwarenessCreative['smsNotification']
    | ITrafficSalesCreative['smsNotification']
) => {
  return richNotification?.description || sms?.messageContent || '';
};

/**
 * Resolves the creative URL to send to the ad supplier.
 *
 * Only accepts an already-hosted URL. It previously accepted a `File` and
 * returned `URL.createObjectURL(file)`, which meant a campaign could launch
 * with a `blob:` URL the ad supplier had no way to fetch — the creative was
 * silently dead on arrival. Files are now uploaded to the asset library at
 * pick time (see `FileUpload`), so by this point a real https URL always
 * exists.
 */
export const getImageUrl = (
  file: string | null | undefined,
  objective: string
): string => {
  if (typeof file === 'string' && file) {
    return file;
  }

  // Fallback to placeholder based on campaign objective
  return objective === EObjective.BrandAwareness
    ? 'https://images.unsplash.com/photo-1521017432531-fbd92d768814?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb2ZmZWUlMjBzaG9wJTIwaW50ZXJpb3J8ZW58MXx8fHwxNzU3MDA4Njg2fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral'
    : 'https://images.unsplash.com/photo-1594622848024-323bdec6ddb1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwY29mZmVlJTIwY3VwJTIwbGF0dGV8ZW58MXx8fHwxNzU3MDI1MzA1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral';
};

/**
 * The hosted still image for the selected creative format. A video ad has no
 * banner of its own — its poster frame is the video itself — so it resolves to
 * null and callers fall back to the objective placeholder for previews.
 */
export const getUploadedImage = (
  selectedFormat?: string,
  displayAd?:
    | IBrandAwarenessCreative['displayAd']
    | ITrafficSalesCreative['displayAd'],
  richNotification?:
    | IBrandAwarenessCreative['richNotification']
    | ITrafficSalesCreative['richNotification']
): string | null => {
  if (selectedFormat === 'display-ad') {
    return displayAd?.selectedAssetUrl || null;
  } else if (selectedFormat === 'rich-notification') {
    return richNotification?.selectedAssetUrl || null;
  }
  return null;
};

/** The hosted video for the selected creative format, when it is a video ad. */
export const getUploadedVideo = (
  selectedFormat?: string,
  videoAd?:
    | IBrandAwarenessCreative['videoAd']
    | ITrafficSalesCreative['videoAd']
): string | null => {
  if (selectedFormat !== 'video-ad') return null;
  return videoAd?.selectedAssetUrl || null;
};

export function isCardExpired(expiry: string): boolean {
  if (!expiry) return false;
  // Expect MM/YY
  const match = /^(\d{2})\/(\d{2})$/.exec(expiry);
  if (!match) return true;

  const month = Number(match[1]);
  const year = Number(match[2]);

  if (month < 1 || month > 12) return true;

  // Convert YY → YYYY (assumes 2000–2099, standard for cards)
  const fullYear = 2000 + year;

  // Cards expire at the *end* of the expiry month
  const expiryDate = endOfMonth(new Date(fullYear, month - 1));

  return isBefore(expiryDate, new Date());
}

export const cleanObject = <T extends object>(obj: T): Partial<T> => {
  const cleaned = {} as Partial<T>;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object' && !Array.isArray(value)) {
          const cleanedNested = cleanObject(value as object);
          if (Object.keys(cleanedNested).length > 0) {
            cleaned[key] = cleanedNested as T[typeof key];
          }
        } else {
          cleaned[key] = value;
        }
      }
    }
  }
  return cleaned;
};
